/**
 * Where sign-in sends the user afterwards (#1352).
 *
 * The auth gate renders `SignInScreen` IN PLACE at the URL the user opened, so
 * `/admin/health?alert=7` — the link in the operator Telegram — is still the
 * address bar while the form is up. Clerk was told to always land on
 * `/dashboard`, which dropped the link. The screen now returns to the page that
 * was asked for.
 *
 * Same-origin, relative paths only. The value can come from the address bar or
 * from a `redirect_url` parameter, i.e. from whoever built the link, so
 * anything that could leave the origin (`//evil.example`, `/\evil`, an absolute
 * URL to another host, a `javascript:` scheme) falls back to the default.
 */

export const DEFAULT_POST_SIGN_IN_PATH = '/dashboard';

/** The auth pages themselves: returning to them would loop. */
const AUTH_PATHS = /^\/(?:sign-in|sign-up)(?:[/?#]|$)/;

/** `/path?search` when `raw` is a safe in-app destination for `origin`, else `null`. */
export function safeReturnPath(
  raw: string | null | undefined,
  origin: string
): string | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > 2048) return null;
  // Control characters and backslashes are parsed differently by browsers and
  // by URL; neither belongs in a path we are about to redirect to.
  if (/[\u0000-\u001f\u007f\\]/.test(raw)) return null;

  let url: URL;
  try {
    // An absolute URL (Clerk's `redirect_url` is one) must be OUR origin; a
    // relative one must start with a single `/`.
    if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) {
      url = new URL(raw);
      if (url.origin !== origin) return null;
    } else {
      if (!raw.startsWith('/') || raw.startsWith('//')) return null;
      url = new URL(raw, origin);
      if (url.origin !== origin) return null;
    }
  } catch {
    return null;
  }

  const path = `${url.pathname}${url.search}`;
  if (AUTH_PATHS.test(path) || path === '/') return null;
  return path;
}

/**
 * Absolute URL for Clerk's `forceRedirectUrl`.
 *
 * On `/sign-in` and `/sign-up` the page itself is no destination, so the
 * `redirect_url` parameter is consulted; elsewhere the current path and search
 * are. The hash is never part of it: `routing="hash"` keeps Clerk's own steps
 * there (`#/factor-one`).
 */
export function postSignInUrl(location: {
  origin: string;
  pathname: string;
  search: string;
}): string {
  const { origin, pathname, search } = location;
  const onAuthPage = AUTH_PATHS.test(pathname);
  const candidate = onAuthPage
    ? new URLSearchParams(search).get('redirect_url')
    : `${pathname}${search}`;
  return `${origin}${safeReturnPath(candidate, origin) ?? DEFAULT_POST_SIGN_IN_PATH}`;
}
